import { isEmpty, isEqual, isNil, isObject, isString } from 'lodash';
import {
  isAddressComponent,
  isAuditCheckboxComponent,
  isAuditTagComponent,
  isAuditTextComponent,
  isDateComponent,
  isDateRangeComponent,
  isEmailComponent,
  isFileComponent,
  isGeolocationComponent,
  isNumberComponent,
  isPersonComponent,
  isSignatureComponent,
  isTimeTrackerComponent,
  isTodoComponent,
  isUrlComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { AddressField } from './AddressField';
import { AuditCheckboxField } from './AuditCheckboxField';
import { AuditTagField } from './AuditTagField';
import { AuditTextField } from './AuditTextField';
import { DateField } from './DateField';
import { DateRangeField } from './DateRangeField';
import { EmailField } from './EmailField';
import { EditableField, Field } from './Field';
import { FileField } from './FileField';
import { GeolocationField } from './GeolocationField';
import { NumberField } from './NumberField';
import { PersonField } from './PersonField';
import { SignatureField } from './SignatureField';
import { TimeTrackerField } from './TimeTrackerField';
import { TodoField } from './TodoField';
import { UrlField } from './UrlField';

export interface Subform {
  workflowTemplateId: string;
  viewTemplateId?: string | null;
  data: Record<string, any>;
}

interface SubformFieldOptions {
  /** Pass the resolved WorkflowTemplate directly (bypasses workflowTemplateRefs lookup). */
  workflowTemplate?: API.WorkflowTemplateFragment;
  /** Components with view-template overrides already applied. When provided,
   *  hasUnfilledRequiredFields() uses these instead of workflowTemplate.components. */
  components?: API.AnyComponent[];
}

export class SubformField implements EditableField {
  readonly subform: Subform;
  readonly workflowTemplate?: API.WorkflowTemplateFragment | undefined;
  /** Components with view-template overrides pre-applied. Used by hasUnfilledRequiredFields(). */
  readonly components?: API.AnyComponent[];
  readonly fieldType = 'SubformField';

  constructor(
    subform: Subform,
    workflowTemplateRefs?:
      | NonNullable<
          NonNullable<API.ListWorkflowEntitiesQuery['list']['refs']>['workflowTemplateRefs']
        >[number][]
      | null,
    options?: SubformFieldOptions,
  ) {
    this.subform = subform;
    if (options?.workflowTemplate) {
      this.workflowTemplate = options.workflowTemplate;
    } else {
      this.workflowTemplate = workflowTemplateRefs?.find(
        w => w.id === subform?.workflowTemplateId,
      ) as API.WorkflowTemplateFragment | undefined;
    }
    this.components = options?.components;
  }

  validate(): string | null {
    if (this.hasUnfilledRequiredFields()) {
      return i18n('card.blocks.subformsInput.hasRequiredErrors');
    }
    return null;
  }

  /**
   * Returns true when the selected subform has at least one unfilled required component.
   * Prefers `components` (view-template overrides pre-applied via applyComponentOverrides).
   * Falls back to `workflowTemplate.components` so that a newly-selected subform (where the
   * view template may not have loaded yet) can still surface base-template required fields.
   * Returns false when neither source is available (safe default).
   */
  hasUnfilledRequiredFields(): boolean {
    const allComponents = this.components ?? this.workflowTemplate?.components ?? [];
    if (!allComponents.length) {
      return false;
    }

    for (const component of allComponents) {
      if (!('required' in component) || !component.required) {
        continue;
      }

      const rawData = this.subform.data[component.id];
      let empty: boolean;

      if (isAuditTagComponent(component)) {
        const field = AuditTagField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isAuditTextComponent(component)) {
        const field = AuditTextField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isAuditCheckboxComponent(component)) {
        const field = AuditCheckboxField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isAddressComponent(component)) {
        const field = AddressField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isDateComponent(component)) {
        const field = DateField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isDateRangeComponent(component)) {
        const field = DateRangeField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isEmailComponent(component)) {
        const field = EmailField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isFileComponent(component)) {
        const field = FileField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isGeolocationComponent(component)) {
        const field = GeolocationField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isNumberComponent(component)) {
        const field = NumberField.fromFieldJson(rawData, component.format);
        empty = isNil(field) || field.isEmpty;
      } else if (isPersonComponent(component)) {
        const field = PersonField.fromFieldJson(rawData, null);
        empty = isNil(field) || field.isEmpty;
      } else if (isSignatureComponent(component)) {
        const field = SignatureField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isTimeTrackerComponent(component)) {
        const field = TimeTrackerField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isTodoComponent(component)) {
        const field = TodoField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else if (isUrlComponent(component)) {
        const field = UrlField.fromFieldJson(rawData);
        empty = isNil(field) || field.isEmpty;
      } else {
        continue;
      }

      if (empty) {
        return true;
      }
    }

    return false;
  }

  isEqual(field: Field): boolean {
    if (!(field instanceof SubformField)) {
      return false;
    }
    // Compare only user-edited data. viewTemplateId and components are derived
    // metadata synced automatically by SubformEditField's useEffect — ignoring them
    // prevents the form from appearing dirty when only those fields changed.
    //
    // Empty values have several equivalent representations depending on where the data
    // came from: a whole field may be `{}` (server-loaded), `null` (re-emitted by the
    // subform form on close), or an absent key; and a nested attribute may be `[]`/`''`
    // (form-hydrated) or simply absent (seeded from a default). normalizeSubformData
    // strips these away recursively so that opening a subform and closing it without
    // edits — or re-hydrating a default-seeded audit field — is not treated as a change.
    return (
      this.subform.workflowTemplateId === field.subform.workflowTemplateId &&
      isEqual(normalizeSubformData(this.subform.data), normalizeSubformData(field.subform.data))
    );
  }

  toFieldJson() {
    return this.subform;
  }

  get isEmpty() {
    return isNil(this.subform?.workflowTemplateId);
  }

  static fromFieldJson(
    jsonBlob: any,
    workflowTemplateRefs?:
      | NonNullable<
          NonNullable<API.ListWorkflowEntitiesQuery['list']['refs']>['workflowTemplateRefs']
        >[number][]
      | null,
  ): SubformField | null {
    if (isSubform(jsonBlob)) {
      return new SubformField(jsonBlob, workflowTemplateRefs);
    } else {
      return null;
    }
  }
}

function isSubform(jsonBlob: any): jsonBlob is Subform {
  return !isNil(jsonBlob) && isString(jsonBlob.workflowTemplateId) && isObject(jsonBlob.data);
}

/**
 * Recursively drops entries whose value is an "empty" representation — nil, an empty
 * string, an empty array, or a (recursively) empty plain object — so that all the
 * equivalent empty forms compare equal. This spans both a whole field (`{}` vs `null` vs
 * absent key) and a nested attribute (`files: []`/`notes: ''` vs simply absent). A field
 * the user actually cleared still differs, because only the non-empty side survives.
 */
function normalizeSubformData(data: Record<string, any>): Record<string, any> {
  return Object.entries(data).reduce<Record<string, any>>((acc, [key, value]) => {
    const normalized = normalizeValue(value);
    if (!isEmptyValue(normalized)) {
      acc[key] = normalized;
    }
    return acc;
  }, {});
}

function normalizeValue(value: any): any {
  if (isObject(value) && !Array.isArray(value)) {
    return normalizeSubformData(value as Record<string, any>);
  }
  return value;
}

function isEmptyValue(value: any): boolean {
  if (isNil(value)) {
    return true;
  }
  if (isString(value) || Array.isArray(value)) {
    return isEmpty(value);
  }
  if (isObject(value)) {
    return isEmpty(value);
  }
  return false;
}
